import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { arrayMoveImmutable } from "array-move";
import "./App.css"; // Stil dosyasını import et

// API'den gelen key'ler ve onların gösterim sırası
const API_KEY_MAPPING = [
  { apiKey: "ALTIN", displayName: "HAS ALTIN", endpoint: "centerGold" },
  { apiKey: "995HAS", displayName: "995 HAS ALTIN", endpoint: "centerGold" },
  { apiKey: "stl2", displayName: "ÇEYREK", endpoint: "onlineSarrafiye" },
  { apiKey: "stl4", displayName: "YARIM", endpoint: "onlineSarrafiye" },
  { apiKey: "stl6", displayName: "TEKLİK", endpoint: "onlineSarrafiye" },
  {
    apiKey: "gram13",
    displayName: "1 GRAM 24 AYAR",
    endpoint: "centerGramGold",
  },
  { apiKey: "stl10", displayName: "ATA LİRA", endpoint: "onlineSarrafiye" },
];

// Bir önceki değeri saklamak için kullanılan custom hook
function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

// Fiyat satırını oluşturan component
const PriceRow = ({
  name,
  buy,
  sell,
  isMobile = false,
  viewMode = "grid",
  apiKey,
  editMode = false,
  onMoveUp,
  onMoveDown,
  itemIndex,
  totalItems,
  isMoving = false,
  animationDirection = null,
}) => {
  const [buyFlash, setBuyFlash] = useState("");
  const [sellFlash, setSellFlash] = useState("");
  const prevBuy = usePrevious(buy);
  const prevSell = usePrevious(sell);

  // Fiyat formatını belirleyen fonksiyon
  const formatPrice = (price, itemName) => {
    const numPrice = parseFloat(price);

    // Has altın ve 995 altın için özel format: 5.574,95 (binlik nokta, ondalık virgül)
    if (
      itemName &&
      (itemName.includes("HAS ALTIN") || itemName.includes("995"))
    ) {
      const formatted = numPrice.toFixed(2);
      const parts = formatted.split(".");
      const integerPart = parts[0];
      const decimalPart = parts[1];

      // Binlikleri nokta ile ayır
      const formattedInteger = integerPart.replace(
        /\B(?=(\d{3})+(?!\d))/g,
        "."
      );

      return `${formattedInteger},${decimalPart}`;
    }
    // Diğer büyük sayılar için binlik ayırıcı ve 2 ondalık
    else if (numPrice >= 1000) {
      return numPrice.toLocaleString("tr-TR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } else {
      // Küçük sayılar için 4 ondalık
      return numPrice.toFixed(4);
    }
  };

  useEffect(() => {
    if (prevBuy !== undefined && buy !== prevBuy) {
      if (parseFloat(buy) > parseFloat(prevBuy)) {
        setBuyFlash("flash-green");
      } else if (parseFloat(buy) < parseFloat(prevBuy)) {
        setBuyFlash("flash-red");
      }
    }

    if (prevSell !== undefined && sell !== prevSell) {
      if (parseFloat(sell) > parseFloat(prevSell)) {
        setSellFlash("flash-green");
      } else if (parseFloat(sell) < parseFloat(prevSell)) {
        setSellFlash("flash-red");
      }
    }

    const timer = setTimeout(() => {
      setBuyFlash("");
      setSellFlash("");
    }, 1200);

    return () => clearTimeout(timer);
  }, [buy, sell, prevBuy, prevSell]);

  if (isMobile) {
    // Liste görünümü
    if (viewMode === "list") {
      return (
        <div className="w-full p-2 mb-2">
          <div
            className={`bg-gradient-to-r from-gray-900/60 to-black/40 rounded-lg p-3 border border-gray-700/50 shadow-md hover:shadow-lg transition-all duration-200 hover:border-gold/30 flex items-center justify-between ${
              isMoving ? `moving-item moving-${animationDirection}` : ""
            }`}
          >
            {/* Sol: Drag Handle / Edit Butonları + İsim */}
            <div className="flex items-center gap-3 transition-all duration-300">
              {editMode ? (
                <>
                  {/* Manuel Butonlar */}
                  <div className="flex bg-black/40 rounded-lg p-1 border border-gold/20 animate-in slide-in-from-left duration-300">
                    <button
                      onClick={() => onMoveUp(apiKey)}
                      disabled={itemIndex === 0}
                      className={`p-2 rounded-md transition-all duration-200 ${
                        itemIndex === 0
                          ? "text-gray-500 cursor-not-allowed opacity-50"
                          : "text-gold hover:bg-gold/20 hover:scale-110 hover:shadow-lg"
                      }`}
                      title="Yukarı taşı"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M5 15l7-7 7 7"
                        />
                      </svg>
                    </button>
                    <div className="w-px bg-gray-600 mx-1"></div>
                    <button
                      onClick={() => onMoveDown(apiKey)}
                      disabled={itemIndex === totalItems - 1}
                      className={`p-2 rounded-md transition-all duration-200 ${
                        itemIndex === totalItems - 1
                          ? "text-gray-500 cursor-not-allowed opacity-50"
                          : "text-gold hover:bg-gold/20 hover:scale-110 hover:shadow-lg"
                      }`}
                      title="Aşağı taşı"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2.5}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>
                  </div>
                </>
              ) : null}
              <h3
                className={`text-sm font-semibold gold-text transition-all duration-300 ${
                  editMode ? "ml-2" : ""
                }`}
              >
                {name}
              </h3>
            </div>

            {/* Sağ: Fiyatlar */}
            <div className="flex gap-4">
              <div className="text-right">
                <div className="text-xs text-gray-400">ALIŞ</div>
                <div
                  className={`text-sm font-mono font-bold text-white price-cell ${buyFlash}`}
                >
                  {formatPrice(buy, name)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-400">SATIŞ</div>
                <div
                  className={`text-sm font-mono font-bold text-white price-cell ${sellFlash}`}
                >
                  {formatPrice(sell, name)}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Grid görünümü
    return (
      <div className="w-1/2 p-2 float-left">
        <div
          className={`bg-gradient-to-br from-gray-900/60 to-black/40 rounded-xl p-4 mb-3 border border-gray-700/50 shadow-lg shadow-black/30 backdrop-blur-sm hover:shadow-xl hover:shadow-black/40 transition-all duration-300 hover:border-gold/30 relative group ${
            isMoving ? `moving-item moving-${animationDirection}` : ""
          }`}
        >
          {/* Edit Butonları */}
          {editMode && (
            <div className="absolute top-2 right-2 bg-black/40 rounded-lg p-1 border border-gold/20 animate-in slide-in-from-right duration-300">
              <div className="flex flex-col gap-1">
                <button
                  onClick={() => onMoveUp(apiKey)}
                  disabled={itemIndex === 0}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    itemIndex === 0
                      ? "text-gray-500 cursor-not-allowed opacity-50"
                      : "text-gold hover:bg-gold/20 hover:scale-110 hover:shadow-lg"
                  }`}
                  title="Yukarı taşı"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M5 15l7-7 7 7"
                    />
                  </svg>
                </button>
                <div className="h-px bg-gray-600 mx-2"></div>
                <button
                  onClick={() => onMoveDown(apiKey)}
                  disabled={itemIndex === totalItems - 1}
                  className={`p-2 rounded-md transition-all duration-200 ${
                    itemIndex === totalItems - 1
                      ? "text-gray-500 cursor-not-allowed opacity-50"
                      : "text-gold hover:bg-gold/20 hover:scale-110 hover:shadow-lg"
                  }`}
                  title="Aşağı taşı"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2.5}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          )}

          {/* Ürün İsmi */}
          <div className="text-center mb-3 pb-2 border-b border-gray-600/30">
            <h3 className="text-base font-bold gold-text truncate tracking-wide">
              {name}
            </h3>
          </div>

          {/* Alış */}
          <div className="mb-3">
            <div className="text-xs font-bold text-gray-300 mb-2 uppercase ml-2 tracking-wider">
              ALIŞ
            </div>
            <div
              className={`text-sm font-mono font-bold text-white price-cell ${buyFlash} bg-black/30 rounded-md px-2 py-1`}
            >
              {formatPrice(buy, name)}
            </div>
          </div>

          {/* Satış */}
          <div>
            <div className="text-xs font-bold text-gray-300 mb-2 uppercase ml-2 tracking-wider">
              SATIŞ
            </div>
            <div
              className={`text-sm font-mono font-bold text-white price-cell ${sellFlash} bg-black/30 rounded-md px-2 py-1`}
            >
              {formatPrice(sell, name)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <tr className="border-b table-row-border">
      <td className="py-1.5 px-6 text-left text-xl md:text-2xl font-light whitespace-nowrap">
        {name}
      </td>
      <td
        className={`py-1.5 px-6 text-center text-xl md:text-2xl font-mono price-cell ${buyFlash}`}
      >
        {formatPrice(buy, name)}
      </td>
      <td
        className={`py-1.5 px-6 text-center text-xl md:text-2xl font-mono price-cell ${sellFlash}`}
      >
        {formatPrice(sell, name)}
      </td>
    </tr>
  );
};

// Hata durumunda gösterilecek component
const ErrorDisplay = ({ message }) => (
  <div className="flex flex-col items-center justify-center flex-grow text-center">
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-24 w-24 text-red-500/70 mb-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
    <h2 className="text-4xl font-bold gold-text mb-2 font-display">
      Veri Alınamadı
    </h2>
    <p className="text-xl text-gray-400">{message}</p>
  </div>
);

// Ana uygulama componenti
function App({ isTVMode = false }) {
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [hasInitialData, setHasInitialData] = useState(false);

  // TV ayarları (sunucudan)
  const [tvSettings, setTvSettings] = useState(null);

  // Görünüm ve sıralama state'leri
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem("btk-view-mode") || "grid";
  });
  const [itemOrder, setItemOrder] = useState(() => {
    const allKeys = API_KEY_MAPPING.map((item) => item.apiKey);
    const saved = localStorage.getItem("btk-item-order");
    if (!saved) return allKeys;
    try {
      const savedOrder = JSON.parse(saved);
      // Kayıtlı sırayı koru ama mapping'de artık olmayan eski key'leri at
      const validSaved = savedOrder.filter((k) => allKeys.includes(k));
      // Mapping'de olup kayıtta olmayan yeni ürünleri (ör. ATA LİRA) sona ekle
      const missing = allKeys.filter((k) => !validSaved.includes(k));
      return [...validSaved, ...missing];
    } catch {
      return allKeys;
    }
  });
  const [editMode, setEditMode] = useState(false);

  // Animasyon state'leri
  const [movingItems, setMovingItems] = useState(new Set());
  const [animatingItems, setAnimatingItems] = useState(new Map());

  // Masaüstü/TV tablosunu ekrana sığdırmak için ölçekleme
  const fitRef = useRef(null); // Kullanılabilir alan (başlık ile alt bar arası)
  const contentRef = useRef(null); // Sabit boyutlu tablo içeriği
  const [tableScale, setTableScale] = useState(1);

  // TV modunda sunucudan ayarları çek
  useEffect(() => {
    if (!isTVMode) return;
    const fetchSettings = () => {
      fetch("/api/tv-settings")
        .then((res) => res.json())
        .then((data) => {
          if (data.items && Array.isArray(data.items)) {
            setTvSettings(data.items);
          }
        })
        .catch(() => {});
    };
    fetchSettings();
    // Ayarlar değişirse canlı yansısın diye periyodik kontrol
    const interval = setInterval(fetchSettings, 10000);
    return () => clearInterval(interval);
  }, [isTVMode]);

  // localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem("btk-view-mode", viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem("btk-item-order", JSON.stringify(itemOrder));
  }, [itemOrder]);

  // Tabloyu, başlık ile alt tarih/saat barı arasındaki alana tam sığacak
  // şekilde ölçekle. Böylece TV çözünürlüğü/overscan ne olursa olsun tüm
  // maddeler mümkün olan en büyük boyutta ve eksiksiz görünür.
  useLayoutEffect(() => {
    const recompute = () => {
      const fit = fitRef.current;
      const content = contentRef.current;
      if (!fit || !content) return;
      const availW = fit.clientWidth;
      const availH = fit.clientHeight;
      // offsetWidth/Height transform'dan etkilenmez -> doğal (ölçeksiz) boyut
      const naturalW = content.offsetWidth;
      const naturalH = content.offsetHeight;
      if (!availW || !availH || !naturalW || !naturalH) return;
      // %2 güvenlik payı: TV overscan'inde kenarların kırpılmaması için
      const next = Math.min(availW / naturalW, availH / naturalH) * 0.98;
      if (next > 0 && Number.isFinite(next)) {
        setTableScale(next);
      }
    };

    recompute();

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(recompute);
      if (fitRef.current) ro.observe(fitRef.current);
      if (contentRef.current) ro.observe(contentRef.current);
    }
    window.addEventListener("resize", recompute);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [data, isTVMode, tvSettings]);

  // Animasyonlu taşıma fonksiyonu
  const animateMove = (apiKey, direction) => {
    const currentIndex = itemOrder.indexOf(apiKey);
    const newIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= itemOrder.length) return;

    // Taşınan item'ları işaretle
    const movingApiKey = apiKey;
    const swappingApiKey = itemOrder[newIndex];

    // Önce animasyon state'lerini ayarla
    setMovingItems(new Set([movingApiKey, swappingApiKey]));
    setAnimatingItems(
      new Map([
        [movingApiKey, direction],
        [swappingApiKey, direction === "up" ? "down" : "up"],
      ])
    );

    // Animasyon tamamlandıktan sonra state'i güncelle
    setTimeout(() => {
      // Önce order'ı güncelle
      const newOrder = arrayMoveImmutable(itemOrder, currentIndex, newIndex);
      setItemOrder(newOrder);

      // Sonra animasyon state'lerini temizle
      setTimeout(() => {
        setMovingItems(new Set());
        setAnimatingItems(new Map());
      }, 50); // Kısa bir gecikme ile temizle
    }, 280); // Animasyon süresinden biraz kısa
  };

  // Manuel sıralama fonksiyonları (butonlar için)
  const moveItemUp = (apiKey) => {
    const currentIndex = itemOrder.indexOf(apiKey);
    if (currentIndex > 0 && !movingItems.has(apiKey)) {
      animateMove(apiKey, "up");
    }
  };

  const moveItemDown = (apiKey) => {
    const currentIndex = itemOrder.indexOf(apiKey);
    if (currentIndex < itemOrder.length - 1 && !movingItems.has(apiKey)) {
      animateMove(apiKey, "down");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      // İlk yükleme değilse loading'i gösterme
      if (hasInitialData) {
        setLoading(false);
      } else {
        setLoading(true);
      }

      try {
        // Tüm endpoint'lerden veri al
        const endpoints = ["centerGold", "centerGramGold", "onlineSarrafiye"];
        const responses = await Promise.all(
          endpoints.map(
            (endpoint) =>
              fetch(`/api/${endpoint}`)
                .then((res) =>
                  res.ok ? res.json() : Promise.reject(`${endpoint} failed`)
                )
                .catch(() => null) // Hata durumunda null döndür
          )
        );

        // Endpoint'ler indeksine göre: [centerGold, centerGramGold, onlineSarrafiye]
        const [centerGoldData, centerGramGoldData, onlineSarrafiyeData] =
          responses;

        // Tüm verileri birleştir
        let combinedData = {};

        // centerGold verisini ekle
        if (centerGoldData?.data) {
          Object.assign(combinedData, centerGoldData.data);
        }

        // centerGramGold verisini ekle
        if (centerGramGoldData?.data) {
          Object.assign(combinedData, centerGramGoldData.data);
        }

        // onlineSarrafiye verisini ekle
        if (onlineSarrafiyeData?.data) {
          Object.assign(combinedData, onlineSarrafiyeData.data);
        }

        // Türkçe formatındaki sayıları normalize eden fonksiyon
        const normalizePrice = (price) => {
          if (typeof price === "string") {
            // API'den gelen format: "5,547.70" -> "5547.70"
            // Önce binlik ayırıcı virgülü kaldır, sonra ondalık noktayı koru
            return price.replace(/,/g, "");
          }
          return price;
        };

        // TV modunda sunucu ayarlarını, mobilde localStorage sırasını kullan
        let orderedMapping;
        if (isTVMode && tvSettings) {
          // Kayıtlı ayarlardaki görünür ürünleri, kayıtlı sıraya göre al
          const visibleFromSettings = tvSettings
            .filter((s) => s.visible)
            .map((s) => API_KEY_MAPPING.find((item) => item.apiKey === s.apiKey))
            .filter(Boolean);
          // Kayıtlı ayarlarda hiç olmayan yeni ürünleri (ör. ATA LİRA) varsayılan
          // olarak görünür kabul edip sona ekle -> deploy'da direkt görünsün
          const settingsKeys = new Set(tvSettings.map((s) => s.apiKey));
          const missing = API_KEY_MAPPING.filter(
            (item) => !settingsKeys.has(item.apiKey)
          );
          orderedMapping = [...visibleFromSettings, ...missing];
        } else {
          orderedMapping = itemOrder
            .map((apiKey) =>
              API_KEY_MAPPING.find((item) => item.apiKey === apiKey)
            )
            .filter(Boolean);
        }

        // API_KEY_MAPPING kullanarak verileri dönüştür
        const filteredAndSortedData = orderedMapping
          .map(({ apiKey, displayName }) => {
            const itemData = combinedData[apiKey];
            if (itemData && itemData.alis && itemData.satis) {
              return {
                name: displayName,
                buy: normalizePrice(itemData.alis),
                sell: normalizePrice(itemData.satis),
                code: itemData.code || apiKey,
                apiKey: apiKey, // Sıralama için apiKey'i de sakla
              };
            }
            return null;
          })
          .filter(Boolean);

        if (filteredAndSortedData.length > 0) {
          setData(filteredAndSortedData);
          setError(null); // Başarılı olunca hatayı temizle
          setHasInitialData(true); // İlk veri başarıyla alındı
        } else {
          throw new Error("API'den boş veri geldi.");
        }
      } catch (e) {
        console.error("Veri alınırken hata oluştu:", e);
        // İlk yükleme sırasında hata olursa farklı mesaj göster
        if (!hasInitialData) {
          setError(
            "Sunucuya bağlanılamıyor. Lütfen daha sonra tekrar deneyin."
          );
        } else {
          // Önceden veri varsa, sadece güncelleme hatası mesajı göster
          setError("Veri güncellenemedi, son bilinen değerler gösteriliyor.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const dataInterval = setInterval(fetchData, 3000); // 3 saniye = 3000ms
    const timeInterval = setInterval(() => setDateTime(new Date()), 1000);
    return () => {
      clearInterval(dataInterval);
      clearInterval(timeInterval);
    };
  }, [hasInitialData, itemOrder, isTVMode, tvSettings]);

  return (
    <div className="flex flex-col h-screen p-4 md:p-4 bg-[#0a0a0a] text-[#f0e6d2] overflow-hidden">
      <header className="text-center mb-1 md:mb-2 relative shrink-0">
        {/* Desktop Başlık */}
        <h1 className="hidden md:block font-display text-5xl md:text-4xl font-bold gold-text tracking-wider">
          Buğra Toprak Kuyumculuk
        </h1>

        {/* Mobil Başlık ve Butonlar */}
        <div className="block md:hidden flex items-center justify-between w-full">
          {/* Sol: Başlık */}
          <div className="text-left ml-3">
            <h1 className="font-display text-2xl font-bold gold-text tracking-wide">
              Buğra Toprak
            </h1>
            <h2 className="font-display text-xl font-semibold gold-text tracking-wide mt-1">
              Kuyumculuk
            </h2>
          </div>

          {/* Sağ: Kontrol Butonları */}
          <div className="opacity-90">
            <div className="flex gap-2">
              {/* Görünüm Değiştirme Butonu */}
              <button
                onClick={() =>
                  setViewMode(viewMode === "grid" ? "list" : "grid")
                }
                className="bg-black/50 hover:bg-black/70 border border-gold/30 hover:border-gold/60 rounded-lg p-2 transition-all duration-300 group"
                title={viewMode === "grid" ? "Liste görünümü" : "Grid görünümü"}
              >
                {viewMode === "grid" ? (
                  // Liste ikonu
                  <svg
                    className="w-5 h-5 text-gold group-hover:scale-110 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6h16M4 12h16M4 18h16"
                    />
                  </svg>
                ) : (
                  // Grid ikonu
                  <svg
                    className="w-5 h-5 text-gold group-hover:scale-110 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                    />
                  </svg>
                )}
              </button>

              {/* Edit Modu Butonu */}
              <button
                onClick={() => setEditMode(!editMode)}
                className={`border rounded-lg p-2 transition-all duration-300 group ${
                  editMode
                    ? "bg-gold/20 border-gold text-gold"
                    : "bg-black/50 hover:bg-black/70 border-gold/30 hover:border-gold/60 text-gold"
                }`}
                title={editMode ? "Düzenleme modundan çık" : "Sırayı düzenle"}
              >
                {editMode ? (
                  // Kaydet ikonu
                  <svg
                    className="w-5 h-5 group-hover:scale-110 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  // Edit ikonu
                  <svg
                    className="w-5 h-5 group-hover:scale-110 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>

        <div className="absolute top-1/2 right-0 -translate-y-1/2 opacity-75 flex items-center space-x-2">
          {loading && <div className="spinner"></div>}
          {!loading && error && (
            <div title={error}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-red-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          )}
        </div>
      </header>

      <main className="flex-grow flex flex-col justify-center min-h-0">
        {loading && !hasInitialData ? (
          <div className="flex items-center justify-center flex-grow">
            <div
              className="spinner"
              style={{ width: "4rem", height: "4rem" }}
            ></div>
          </div>
        ) : data.length > 0 ? (
          <>
            {/* Desktop Tablo - ekrana tam sığacak şekilde ölçeklenir */}
            <div
              ref={fitRef}
              className="hidden md:flex flex-1 min-h-0 w-full items-center justify-center overflow-hidden"
            >
              <div
                ref={contentRef}
                style={{
                  width: "880px",
                  transform: `scale(${tableScale})`,
                  transformOrigin: "center center",
                }}
                className="shrink-0 bg-black/30 rounded-lg overflow-hidden"
              >
                <table className="w-full text-white">
                  <thead>
                    <tr className="border-b table-row-border">
                      <th className="py-1.5 px-6 text-left text-xl md:text-2xl font-semibold gold-text tracking-widest">
                        ÜRÜN
                      </th>
                      <th className="py-1.5 px-6 text-center text-xl md:text-2xl font-semibold gold-text tracking-widest">
                        ALIŞ
                      </th>
                      <th className="py-1.5 px-6 text-center text-xl md:text-2xl font-semibold gold-text tracking-widest">
                        SATIŞ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.map((item, index) => (
                      <PriceRow
                        key={item.code || index}
                        name={item.name}
                        buy={item.buy}
                        sell={item.sell}
                        isMobile={false}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mobil Görünüm */}
            <div className="block md:hidden w-full max-w-4xl mx-auto">
              <div className={viewMode === "grid" ? "clearfix" : "space-y-1"}>
                {data.map((item, index) => {
                  const orderIndex = itemOrder.indexOf(item.apiKey);
                  return (
                    <PriceRow
                      key={item.apiKey || item.code || index}
                      name={item.name}
                      buy={item.buy}
                      sell={item.sell}
                      apiKey={item.apiKey}
                      isMobile={true}
                      viewMode={viewMode}
                      editMode={editMode}
                      onMoveUp={moveItemUp}
                      onMoveDown={moveItemDown}
                      itemIndex={orderIndex}
                      totalItems={data.length}
                      isMoving={movingItems.has(item.apiKey)}
                      animationDirection={animatingItems.get(item.apiKey)}
                    />
                  );
                })}
              </div>
              {/* Clear float if grid mode */}
              {viewMode === "grid" && <div className="clear-both"></div>}
            </div>
          </>
        ) : (
          <ErrorDisplay message={error || "Başlangıç verisi yüklenemedi."} />
        )}
      </main>

      <footer className="w-full mt-1 md:mt-2 shrink-0">
        <div className="flex items-center justify-between w-full max-w-6xl mx-auto px-2 md:px-6 py-1 leading-none text-xs md:text-sm font-medium text-white">
          <span className="truncate">
            {dateTime.toLocaleDateString("tr-TR", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </span>
          <span className="font-mono tabular-nums ml-2">
            {dateTime.toLocaleTimeString("tr-TR")}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;
